import React, { useState, useEffect } from "react";
import { Drawer, Button } from "antd";
import {
    CloseOutlined,
    CalendarOutlined,
    CheckCircleFilled,
} from "@ant-design/icons";
import { Tokens } from "Shared/Tokens";

export const CUTOFF_PRESETS = [
    {
        value: "2026-01-01",
        title: "2026 Year-to-Date",
        badge: "Jan 1, 2026",
        subtitle: "Excludes 2021–2024 exited cycles · Recommended",
    },
    {
        value: "2026-08-24",
        title: "Current cycle",
        badge: "Aug 24, 2026",
        subtitle: "Since your last full portfolio reset",
    },
    {
        value: "2026-10-01",
        title: "Last 30 days",
        badge: "Oct 1, 2026",
        subtitle: "Recent month closed trades & realized gains",
    },
    {
        value: "2025-01-01",
        title: "2025–2026 history",
        badge: "Jan 1, 2025",
        subtitle: "Two-year realized performance",
    },
    {
        value: "all",
        title: "All time",
        badge: "Full ledger",
        subtitle: "Every trade since 2021 (includes exited 2021–2024 coins)",
    },
];

const MONTHS = [
    { num: "01", label: "Jan" },
    { num: "02", label: "Feb" },
    { num: "03", label: "Mar" },
    { num: "04", label: "Apr" },
    { num: "05", label: "May" },
    { num: "06", label: "Jun" },
    { num: "07", label: "Jul" },
    { num: "08", label: "Aug" },
    { num: "09", label: "Sep" },
    { num: "10", label: "Oct" },
    { num: "11", label: "Nov" },
    { num: "12", label: "Dec" },
];

const YEARS = ["2026", "2025", "2024", "2023", "2022"];
const DAYS = ["01", "05", "10", "15", "20", "24", "28"];

export const formatCutoffLabel = (cutoffDate) => {
    if (!cutoffDate || cutoffDate === "all") {
        return "All time";
    }
    if (cutoffDate === "2026-01-01") {
        return "Since Jan 1, 2026";
    }
    if (cutoffDate === "2026-08-24") {
        return "Cycle · Aug 24, 2026";
    }
    if (cutoffDate === "2026-10-01") {
        return "Since Oct 1, 2026";
    }
    if (cutoffDate === "2025-01-01") {
        return "Since Jan 1, 2025";
    }
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(cutoffDate).trim());
    if (match) {
        const monthObj = MONTHS.find((m) => m.num === match[2]);
        const dayNum = parseInt(match[3], 10);
        if (monthObj && !Number.isNaN(dayNum)) {
            return `Since ${monthObj.label} ${dayNum}, ${match[1]}`;
        }
    }
    return `Since ${cutoffDate}`;
};

const parseCustomParts = (cutoffDate) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(cutoffDate || ""));
    if (match) {
        return { year: match[1], month: match[2], day: match[3] };
    }
    return { year: "2026", month: "08", day: "24" };
};

const CutoffDateDrawer = ({
    open,
    onClose,
    value = "2026-01-01",
    onChange,
}) => {
    const activeValue = value || "2026-01-01";
    const isPreset = CUTOFF_PRESETS.some((p) => p.value === activeValue);
    const [showCustom, setShowCustom] = useState(!isPreset);
    const initialParts = parseCustomParts(activeValue);
    const [customYear, setCustomYear] = useState(initialParts.year);
    const [customMonth, setCustomMonth] = useState(initialParts.month);
    const [customDay, setCustomDay] = useState(initialParts.day);
    const [rawInputDate, setRawInputDate] = useState(
        activeValue === "all" ? "2026-01-01" : activeValue
    );

    useEffect(() => {
        const parts = parseCustomParts(activeValue);
        setCustomYear(parts.year);
        setCustomMonth(parts.month);
        setCustomDay(parts.day);
        if (activeValue && activeValue !== "all") {
            setRawInputDate(activeValue);
        }
        if (!CUTOFF_PRESETS.some((p) => p.value === activeValue)) {
            setShowCustom(true);
        }
    }, [activeValue, open]);

    const handleSelectPreset = (presetVal) => {
        if (typeof onChange === "function") {
            onChange(presetVal);
        }
        if (typeof onClose === "function") {
            onClose();
        }
    };

    const handlePillChange = (nextYear, nextMonth, nextDay) => {
        const y = nextYear || customYear;
        const m = nextMonth || customMonth;
        const d = nextDay || customDay;
        setCustomYear(y);
        setCustomMonth(m);
        setCustomDay(d);
        setRawInputDate(`${y}-${m}-${d}`);
    };

    const handleApplyCustom = () => {
        const candidate = String(
            rawInputDate || `${customYear}-${customMonth}-${customDay}`
        ).trim();
        if (/^\d{4}-\d{2}-\d{2}$/.test(candidate)) {
            if (typeof onChange === "function") {
                onChange(candidate);
            }
            if (typeof onClose === "function") {
                onClose();
            }
        }
    };

    return (
        <Drawer
            open={open}
            onClose={onClose}
            push={false}
            placement="bottom"
            height="auto"
            closable
            closeIcon={
                <CloseOutlined style={{ color: Tokens.textSecondary }} />
            }
            maskClosable
            maskStyle={{ backgroundColor: "rgba(0, 0, 0, 0.75)" }}
            title={
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        color: Tokens.textPrimary,
                        fontSize: "15px",
                        fontWeight: 500,
                    }}
                >
                    <CalendarOutlined style={{ color: Tokens.accent }} />
                    <span>P&amp;L baseline date</span>
                </div>
            }
            headerStyle={{
                backgroundColor: Tokens.bg,
                borderBottom: `1px solid ${Tokens.hairline}`,
                padding: "14px 16px",
            }}
            bodyStyle={{
                backgroundColor: Tokens.bg,
                padding: "16px 16px 24px",
                maxHeight: "82vh",
                overflowY: "auto",
            }}
        >
            <div
                style={{
                    maxWidth: Tokens.containerMaxWidth,
                    margin: "0 auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                }}
            >
                <div
                    style={{
                        fontSize: "12px",
                        color: Tokens.textSecondary,
                        lineHeight: 1.5,
                        backgroundColor: Tokens.card,
                        border: `1px solid ${Tokens.hairline}`,
                        borderRadius: Tokens.radiusCard,
                        padding: "10px 12px",
                    }}
                >
                    Sets the start date for{" "}
                    <span
                        style={{ color: Tokens.textPrimary, fontWeight: 500 }}
                    >
                        Realized P&amp;L
                    </span>{" "}
                    and closed trade history. Active holdings always reflect the
                    true cost of the coins currently in your wallet.
                </div>

                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                    }}
                >
                    {CUTOFF_PRESETS.map((preset) => {
                        const selected =
                            activeValue === preset.value && !showCustom;
                        return (
                            <button
                                key={preset.value}
                                type="button"
                                data-testid={`cutoff-preset-${preset.value}`}
                                onClick={() => {
                                    setShowCustom(false);
                                    handleSelectPreset(preset.value);
                                }}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: "12px",
                                    width: "100%",
                                    minHeight: "56px",
                                    textAlign: "left",
                                    padding: "10px 14px",
                                    backgroundColor: selected
                                        ? "rgba(217, 119, 6, 0.1)"
                                        : Tokens.card,
                                    border: `1px solid ${
                                        selected
                                            ? Tokens.accent
                                            : Tokens.hairline
                                    }`,
                                    borderRadius: Tokens.radiusCard,
                                    cursor: "pointer",
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "2px",
                                        minWidth: 0,
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                            flexWrap: "wrap",
                                        }}
                                    >
                                        <span
                                            style={{
                                                fontSize: "14px",
                                                fontWeight: 500,
                                                color: Tokens.textPrimary,
                                            }}
                                        >
                                            {preset.title}
                                        </span>
                                        <span
                                            style={{
                                                fontSize: "11px",
                                                fontFamily: Tokens.fontMono,
                                                fontVariantNumeric:
                                                    "tabular-nums",
                                                color: selected
                                                    ? Tokens.accent
                                                    : Tokens.textMuted,
                                                backgroundColor: Tokens.surface,
                                                padding: "1px 6px",
                                                borderRadius: Tokens.radiusTag,
                                            }}
                                        >
                                            {preset.badge}
                                        </span>
                                    </div>
                                    <span
                                        style={{
                                            fontSize: "12px",
                                            color: Tokens.textSecondary,
                                        }}
                                    >
                                        {preset.subtitle}
                                    </span>
                                </div>
                                <CheckCircleFilled
                                    style={{
                                        fontSize: "16px",
                                        color: selected
                                            ? Tokens.accent
                                            : Tokens.hairlineStrong,
                                        flexShrink: 0,
                                    }}
                                />
                            </button>
                        );
                    })}

                    <button
                        type="button"
                        data-testid="cutoff-custom-toggle"
                        onClick={() => setShowCustom((prev) => !prev)}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            width: "100%",
                            minHeight: "48px",
                            padding: "10px 14px",
                            backgroundColor:
                                showCustom || !isPreset
                                    ? "rgba(217, 119, 6, 0.1)"
                                    : Tokens.card,
                            border: `1px solid ${
                                showCustom || !isPreset
                                    ? Tokens.accent
                                    : Tokens.hairline
                            }`,
                            borderRadius: Tokens.radiusCard,
                            cursor: "pointer",
                            textAlign: "left",
                        }}
                    >
                        <div>
                            <div
                                style={{
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    color: Tokens.textPrimary,
                                }}
                            >
                                Custom start date
                            </div>
                            <div
                                style={{
                                    fontSize: "12px",
                                    color: Tokens.textSecondary,
                                }}
                            >
                                Pick any year, month, and day
                            </div>
                        </div>
                        <span
                            style={{
                                fontSize: "12px",
                                fontFamily: Tokens.fontMono,
                                color: Tokens.accent,
                            }}
                        >
                            {rawInputDate}
                        </span>
                    </button>
                </div>

                {showCustom && (
                    <div
                        style={{
                            backgroundColor: Tokens.card,
                            border: `1px solid ${Tokens.hairline}`,
                            borderRadius: Tokens.radiusCard,
                            padding: "12px",
                            display: "flex",
                            flexDirection: "column",
                            gap: "12px",
                        }}
                    >
                        <div>
                            <div
                                style={{
                                    fontSize: "11px",
                                    color: Tokens.textMuted,
                                    marginBottom: "6px",
                                }}
                            >
                                Year
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    gap: "6px",
                                    flexWrap: "wrap",
                                }}
                            >
                                {YEARS.map((y) => (
                                    <button
                                        key={y}
                                        type="button"
                                        onClick={() =>
                                            handlePillChange(y, null, null)
                                        }
                                        style={{
                                            minHeight: "34px",
                                            padding: "4px 12px",
                                            borderRadius: Tokens.radiusControl,
                                            border: `1px solid ${
                                                customYear === y
                                                    ? Tokens.accent
                                                    : Tokens.hairline
                                            }`,
                                            backgroundColor:
                                                customYear === y
                                                    ? "rgba(217, 119, 6, 0.15)"
                                                    : Tokens.surface,
                                            color:
                                                customYear === y
                                                    ? Tokens.textPrimary
                                                    : Tokens.textSecondary,
                                            fontFamily: Tokens.fontMono,
                                            fontSize: "12px",
                                            cursor: "pointer",
                                        }}
                                    >
                                        {y}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <div
                                style={{
                                    fontSize: "11px",
                                    color: Tokens.textMuted,
                                    marginBottom: "6px",
                                }}
                            >
                                Month
                            </div>
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(6, 1fr)",
                                    gap: "6px",
                                }}
                            >
                                {MONTHS.map((m) => (
                                    <button
                                        key={m.num}
                                        type="button"
                                        onClick={() =>
                                            handlePillChange(null, m.num, null)
                                        }
                                        style={{
                                            minHeight: "34px",
                                            padding: "4px 0",
                                            borderRadius: Tokens.radiusControl,
                                            border: `1px solid ${
                                                customMonth === m.num
                                                    ? Tokens.accent
                                                    : Tokens.hairline
                                            }`,
                                            backgroundColor:
                                                customMonth === m.num
                                                    ? "rgba(217, 119, 6, 0.15)"
                                                    : Tokens.surface,
                                            color:
                                                customMonth === m.num
                                                    ? Tokens.textPrimary
                                                    : Tokens.textSecondary,
                                            fontSize: "12px",
                                            cursor: "pointer",
                                        }}
                                    >
                                        {m.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <div
                                style={{
                                    fontSize: "11px",
                                    color: Tokens.textMuted,
                                    marginBottom: "6px",
                                }}
                            >
                                Day or exact date (YYYY-MM-DD)
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    gap: "6px",
                                    flexWrap: "wrap",
                                    marginBottom: "8px",
                                }}
                            >
                                {DAYS.map((d) => (
                                    <button
                                        key={d}
                                        type="button"
                                        onClick={() =>
                                            handlePillChange(null, null, d)
                                        }
                                        style={{
                                            minHeight: "32px",
                                            padding: "2px 10px",
                                            borderRadius: Tokens.radiusControl,
                                            border: `1px solid ${
                                                customDay === d
                                                    ? Tokens.accent
                                                    : Tokens.hairline
                                            }`,
                                            backgroundColor:
                                                customDay === d
                                                    ? "rgba(217, 119, 6, 0.15)"
                                                    : Tokens.surface,
                                            color:
                                                customDay === d
                                                    ? Tokens.textPrimary
                                                    : Tokens.textSecondary,
                                            fontFamily: Tokens.fontMono,
                                            fontSize: "12px",
                                            cursor: "pointer",
                                        }}
                                    >
                                        {d}
                                    </button>
                                ))}
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    gap: "8px",
                                    alignItems: "center",
                                }}
                            >
                                <input
                                    type="date"
                                    aria-label="Custom cutoff date"
                                    data-testid="binance-cutoff-date-input"
                                    value={rawInputDate}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setRawInputDate(val);
                                        const parts = parseCustomParts(val);
                                        setCustomYear(parts.year);
                                        setCustomMonth(parts.month);
                                        setCustomDay(parts.day);
                                    }}
                                    style={{
                                        flex: 1,
                                        height: "40px",
                                        backgroundColor: Tokens.surface,
                                        color: Tokens.textPrimary,
                                        border: `1px solid ${Tokens.hairline}`,
                                        borderRadius: Tokens.radiusControl,
                                        padding: "0 10px",
                                        fontFamily: Tokens.fontMono,
                                        fontSize: "13px",
                                    }}
                                />
                                <Button
                                    type="primary"
                                    data-testid="cutoff-apply-custom-btn"
                                    onClick={handleApplyCustom}
                                    style={{
                                        height: "40px",
                                        borderRadius: Tokens.radiusControl,
                                        fontWeight: 500,
                                    }}
                                >
                                    Apply date
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Drawer>
    );
};

export default CutoffDateDrawer;
